/* ipl-pdp-a11y.js
   Product-page corrections that sit on top of sections/ipl-product-page.liquid
   without forking it. Loaded from sections/ipl-extras.liquid, which already
   renders on every product template.

   1. Heading levels. The buy card owns the only <h1> and every title under it
      was an <h3>, so the outline skipped a level the whole way down. Worse, the
      visible accordion titles were bare <summary> text with no heading
      semantics at all, so a screen-reader user could not jump between the
      description sections. Each rendered section title becomes an <h2>.
   2. A polite live region. Price, stock and the "unavailable" notice all change
      silently when an option is picked; nothing was announced. One region
      carries the whole new state so the three do not talk over each other.
   3. Stock wording. On products that do not track inventory, "Out of stock"
      can only ever be a data artefact, so it is normalised to "In stock".
      Products that genuinely track stock are left alone and stay truthful.
   4. Panel size to scale. Cut-to-size products list up to a dozen panel sizes
      with no image that conveys how big any of them is.
   5. Combinations that do not exist are marked before they are clicked, and the
      last valid price is cleared instead of being left on screen next to
      "Unavailable in this combination".
   6. An ex-VAT figure and a price per square metre, for trade buyers comparing
      sizes.
   7. The chosen variant is written into the URL, so a configured spec can be
      shared, bookmarked and restored by the back button.

   5-7 need a variant index. It is read from the ProductGroup JSON-LD already on
   the page, so nothing extra is downloaded; if that markup ever changes shape,
   buildIndex returns null and those four features quietly switch off.

   17 Sep 2026 fix. buildIndex used to recover a variant title by splitting the
   JSON-LD name on " - " and keeping the last segment. 463 of the 1,943 active
   products carry " - " INSIDE an option value ("3mm (10m2 Roll - 10m x 1m)",
   "800mm - Custom Cut (+£10.00)", "10mm / 1800gsm - pack of 1"), so the key
   came out as a fragment, nothing ever matched, and every one of those product
   pages showed "Price on request" with every option struck through as though
   the product could not be bought. The group name is now stripped as a prefix
   instead, which cannot be confused by hyphens further along the string, and
   keyFor tidies the lookup side so values with doubled spaces match too.
*/
(function () {
  'use strict';

  var CFG = window.IPLPDP || {};
  var root = document.getElementById('iplp');
  if (!root) return;

  /* panel sizes are written "2000 x 1000mm"; the unit is optional and may sit
     after either figure, so old bare-number values keep working too */
  var DIM = /^\s*(\d+(?:\.\d+)?)\s*(?:mm)?\s*[x×]\s*(\d+(?:\.\d+)?)\s*(?:mm)?\s*$/i;
  var SVGNS = 'http://www.w3.org/2000/svg';
  var VAT = typeof CFG.vatRate === 'number' ? CFG.vatRate : 0.2;
  var NO_PRICE = 'Price on request';

  function each(list, fn) { Array.prototype.forEach.call(list, fn); }
  function tidy(s) { return (s || '').replace(/\s+/g, ' ').trim(); }

  /* ---------------------------------------------------------------
     Variant index, read from the JSON-LD already in the page
     --------------------------------------------------------------- */

  var INDEX = null;

  function buildIndex() {
    var map = {};
    var n = 0;
    each(document.querySelectorAll('script[type="application/ld+json"]'), function (s) {
      var data;
      try { data = JSON.parse(s.textContent); } catch (e) { return; }

      /* Every variant is named "<product group name> - <variant title>", and the
         variant title may itself contain " - ". Splitting on the separator is
         therefore ambiguous; stripping the known group name as a prefix is not. */
      var groups = [];
      (function collect(o) {
        if (Array.isArray(o)) { o.forEach(collect); return; }
        if (!o || typeof o !== 'object') return;
        if (o['@type'] === 'ProductGroup' && o.name) groups.push(String(o.name));
        Object.keys(o).forEach(function (k) { collect(o[k]); });
      })(data);

      function variantTitle(name) {
        for (var i = 0; i < groups.length; i++) {
          var prefix = groups[i] + ' - ';
          if (name.slice(0, prefix.length) === prefix) return name.slice(prefix.length);
        }
        /* no group name to lean on: fall back to the old last-segment split */
        var parts = name.split(' - ');
        return parts.length > 1 ? parts[parts.length - 1] : null;
      }

      (function walk(o) {
        if (Array.isArray(o)) { o.forEach(walk); return; }
        if (!o || typeof o !== 'object') return;
        if (o['@type'] === 'Product' && o.offers && o.name) {
          var offer = Array.isArray(o.offers) ? o.offers[0] : o.offers;
          var m = String(offer && offer.url || '').match(/variant=(\d+)/);
          var vt = variantTitle(String(o.name));
          if (m && vt) {
            var price = parseFloat(offer.price);
            map[tidy(vt)] = { id: m[1], price: isNaN(price) ? null : price };
            n++;
          }
        }
        Object.keys(o).forEach(function (k) { walk(o[k]); });
      })(data);
    });
    return n > 1 ? map : null;
  }

  function optionBlocks() { return root.querySelectorAll('.iplp__opt'); }

  function selectedValues() {
    var vals = [];
    each(optionBlocks(), function (o) {
      var s = o.querySelector('[data-optselect]');
      if (s) { vals.push(s.value); return; }
      var a = o.querySelector('[data-optpill].active');
      vals.push(a ? a.getAttribute('data-value') : null);
    });
    return vals;
  }

  /* Shopify builds a variant title by joining the option values with " / ".
     The map is keyed through tidy(), so the lookup side is tidied to match:
     several option values carry doubled spaces ("10m Length  x 0.6m Wide"). */
  function keyFor(vals) {
    return vals.map(function (v) { return tidy(v); }).join(' / ');
  }

  function currentRecord() {
    if (!INDEX) return null;
    var vals = selectedValues();
    for (var i = 0; i < vals.length; i++) { if (vals[i] == null) return null; }
    return INDEX[keyFor(vals)] || null;
  }

  function currencySymbol() {
    var el = document.getElementById('iplpBuyPrice');
    var m = el && el.textContent.match(/[^\d\s.,]+/);
    return m ? m[0] : '£';
  }

  function money(n) {
    var p = n.toFixed(2).split('.');
    return currencySymbol() + p[0].replace(/\B(?=(\d{3})+$)/g, ',') + '.' + p[1];
  }

  /* ---------------------------------------------------------------
     1. Heading levels
     --------------------------------------------------------------- */

  function toH2(el) {
    if (!el || el.tagName !== 'H3') return;
    var h2 = document.createElement('h2');
    if (el.className) h2.className = el.className;
    while (el.firstChild) h2.appendChild(el.firstChild);
    el.parentNode.replaceChild(h2, el);
  }

  function upgradeHeadings() {
    /* hardcoded titles: crosslink card, quote modal, and the "need help
       choosing" CTA that ipl-boost.js injects */
    each(document.querySelectorAll(
      '.iplp__xwrap > h3, .iplp__qm-head > h3, .iplp__fit > h3'
    ), toH2);

    /* accordion titles are built as plain <summary> text; a single h1-h6 is
       valid summary content, so wrap it rather than replacing the summary */
    each(document.querySelectorAll('.iplp__acc > details > summary'), function (sum) {
      if (sum.querySelector('.iplp__acc-h')) return;
      var h = document.createElement('h2');
      h.className = 'iplp__acc-h';
      while (sum.firstChild) h.appendChild(sum.firstChild);
      sum.appendChild(h);
    });

    /* the accordion builder moves the description content out of #iplpDescSrc
       but leaves the original headings behind in the hidden div, where they
       still count towards the document outline */
    var src = document.getElementById('iplpDescSrc');
    var acc = document.getElementById('iplpAcc');
    if (src && acc && acc.children.length && src.parentNode) {
      src.parentNode.removeChild(src);
    }
  }

  /* ---------------------------------------------------------------
     2. Live region
     --------------------------------------------------------------- */

  var announcer = null;
  var lastSaid = '';
  var sayTimer = null;

  function ensureAnnouncer() {
    if (announcer) return announcer;
    announcer = document.createElement('p');
    announcer.id = 'iplpAnnounce';
    announcer.className = 'iplp__sr';
    announcer.setAttribute('role', 'status');
    announcer.setAttribute('aria-live', 'polite');
    announcer.setAttribute('aria-atomic', 'true');
    (document.querySelector('.iplp__buy') || root).appendChild(announcer);
    return announcer;
  }

  function selectionText() {
    var parts = [];
    each(optionBlocks(), function (o) {
      var lab = o.querySelector('.iplp__opt-label');
      if (lab && !lab.hidden) {
        if (tidy(lab.textContent)) parts.push(tidy(lab.textContent));
        return;
      }
      /* long option lists are re-rendered as labelled dimension axes */
      each(o.querySelectorAll('.iplp__axis-label'), function (al) {
        if (tidy(al.textContent)) parts.push(tidy(al.textContent));
      });
    });
    return parts.join(', ');
  }

  function announce() {
    var price = tidy((document.getElementById('iplpBuyPrice') || {}).textContent);
    var stock = tidy((document.getElementById('iplpStock') || {}).textContent);
    if (/unavailable/i.test(stock) || price === NO_PRICE) price = '';
    var extras = tidy((document.getElementById('iplpPriceExtras') || {}).textContent);
    var msg = [selectionText(), price, extras, stock].filter(Boolean).join('. ');
    if (!msg || msg === lastSaid) return;
    lastSaid = msg;
    ensureAnnouncer().textContent = msg + '.';
  }

  function announceSoon() {
    clearTimeout(sayTimer);
    sayTimer = setTimeout(announce, 350);
  }

  /* ---------------------------------------------------------------
     3. Stock wording
     --------------------------------------------------------------- */

  function normaliseStock() {
    if (!CFG.neverOutOfStock) return;
    var el = document.getElementById('iplpStock');
    if (!el) return;
    var t = tidy(el.textContent).toLowerCase();
    /* "Unavailable in this combination" is about an option pair that does not
       exist, not about stock, and must be left alone */
    if (t !== 'out of stock' && t !== 'sold out') return;
    el.textContent = 'In stock';
    el.classList.remove('is-out');
    ['iplpAtc', 'iplpBarAtc', 'iplpBuyNow'].forEach(function (id) {
      var b = document.getElementById(id);
      if (!b) return;
      b.disabled = false;
      if (tidy(b.textContent).toLowerCase() === 'sold out') b.textContent = 'Add to basket';
    });
  }

  /* ---------------------------------------------------------------
     5. Mark combinations that do not exist, and clear the stale price
     --------------------------------------------------------------- */

  function markUnavailable() {
    if (!INDEX) return;
    var vals = selectedValues();
    each(optionBlocks(), function (o, i) {
      each(o.querySelectorAll('[data-optpill]'), function (p) {
        var trial = vals.slice();
        trial[i] = p.getAttribute('data-value');
        var exists = !!INDEX[keyFor(trial)];
        p.classList.toggle('is-na', !exists);
        if (exists) {
          p.removeAttribute('aria-disabled');
          p.removeAttribute('title');
        } else {
          p.setAttribute('aria-disabled', 'true');
          p.setAttribute('title', 'Not available with the current selection');
        }
      });
      var s = o.querySelector('[data-optselect]');
      if (!s) return;
      each(s.options, function (op) {
        if (!op.getAttribute('data-base')) op.setAttribute('data-base', op.textContent);
        var base = op.getAttribute('data-base');
        var trial = vals.slice();
        trial[i] = op.value;
        /* left selectable on purpose: choosing one still explains itself */
        op.textContent = INDEX[keyFor(trial)] ? base : base + ' — not in this size';
      });
    });
  }

  /* ---------------------------------------------------------------
     10. Units on the panel-size labels
     The option values are stored as bare numbers ("2000 x 1000"), so the picker
     never says what the figures are in. Only the rendered label is changed;
     data-value and the <select> value stay untouched because they are what the
     variant lookup matches on. Idempotent, so if the option values themselves
     are ever renamed to carry the unit this quietly stops doing anything.
     --------------------------------------------------------------- */

  function withUnit(v) {
    if (!v) return v;
    return DIM.test(v) && !/mm\s*$/i.test(v) ? v + 'mm' : v;
  }

  /* ---------------------------------------------------------------
     11. Option values in numeric order
     Shopify keeps option values in the order they were created, which across
     these products means thicknesses starting at 40mm and panel sizes starting
     at 2000 x 500. Sorting the rendered controls fixes every product at once
     without touching product data, and without moving the default variant.
     Values that are not a measurement (e.g. "Custom Size - priced by
     calculator") sort to the end and keep their relative order.
     --------------------------------------------------------------- */

  function sortKey(v) {
    var s = String(v);
    var d = s.match(DIM);
    if (d) return [1, parseFloat(d[1]), parseFloat(d[2])];
    var t = s.match(/^\s*([\d.]+)\s*mm\s*$/i);
    if (t) return [0, parseFloat(t[1]), 0];
    return [2, Infinity, Infinity];
  }

  function cmpValues(a, b) {
    var x = sortKey(a);
    var y = sortKey(b);
    return (x[0] - y[0]) || (x[1] - y[1]) || (x[2] - y[2]);
  }

  function reorderList(nodes, valueOf, append) {
    var current = Array.prototype.slice.call(nodes);
    if (current.length < 2) return;
    var sorted = current.slice().sort(function (a, b) {
      return cmpValues(valueOf(a), valueOf(b));
    });
    var moved = sorted.some(function (n, i) { return n !== current[i]; });
    /* appendChild moves the existing nodes, so listeners and state survive */
    if (moved) sorted.forEach(append);
  }

  function sortOptionValues() {
    each(optionBlocks(), function (o) {
      each(o.querySelectorAll('.iplp__pills'), function (wrap) {
        reorderList(
          wrap.querySelectorAll('[data-optpill]'),
          function (p) { return p.getAttribute('data-value'); },
          function (p) { wrap.appendChild(p); }
        );
      });
      var s = o.querySelector('[data-optselect]');
      if (!s) return;
      var keep = s.value;
      reorderList(
        s.options,
        function (op) { return op.value; },
        function (op) { s.appendChild(op); }
      );
      s.value = keep;
    });
  }

  function labelUnits() {
    each(optionBlocks(), function (o) {
      each(o.querySelectorAll('[data-optpill]'), function (p) {
        var raw = p.getAttribute('data-value');
        var want = withUnit(raw);
        if (want === raw) return;
        var span = p.querySelector('span:not(.iplp__pill-price)') || p;
        if (tidy(span.textContent) !== want) span.textContent = want;
      });
      var cur = o.querySelector('[data-optval]');
      if (cur) {
        var want2 = withUnit(tidy(cur.textContent));
        if (tidy(cur.textContent) !== want2) cur.textContent = want2;
      }
      var s = o.querySelector('[data-optselect]');
      if (!s) return;
      each(s.options, function (op) {
        if (!op.getAttribute('data-base')) op.setAttribute('data-base', withUnit(op.value));
      });
    });
  }

  function syncPrice() {
    var priceEl = document.getElementById('iplpBuyPrice');
    if (!priceEl || !INDEX) return;
    /* the section leaves the previous variant's price on screen when a
       combination does not exist, which reads as if that price applies */
    if (!currentRecord() && tidy(priceEl.textContent) !== NO_PRICE) {
      priceEl.textContent = NO_PRICE;
    }
  }

  /* ---------------------------------------------------------------
     6. Ex-VAT and price per square metre
     --------------------------------------------------------------- */

  function ensurePriceExtras() {
    var el = document.getElementById('iplpPriceExtras');
    if (el) return el;
    var row = root.querySelector('.iplp__pricerow');
    if (!row || !row.parentNode) return null;
    el = document.createElement('p');
    el.id = 'iplpPriceExtras';
    el.className = 'iplp__priceextras';
    row.parentNode.insertBefore(el, row.nextSibling);
    return el;
  }

  function updatePriceExtras() {
    var host = ensurePriceExtras();
    if (!host) return;
    var rec = currentRecord();
    if (!rec || rec.price == null) { host.hidden = true; host.textContent = ''; return; }

    var bits = [money(rec.price / (1 + VAT)) + ' ex VAT'];
    var dim = null;
    selectedValues().forEach(function (v) {
      var m = v && v.match(DIM);
      if (m) dim = m;
    });
    if (dim) {
      var area = (parseFloat(dim[1]) * parseFloat(dim[2])) / 1000000;
      if (area > 0) bits.push(money(rec.price / area) + ' per m² inc VAT');
    }
    host.hidden = false;
    host.textContent = bits.join('  ·  ');
  }

  /* ---------------------------------------------------------------
     7. Keep the chosen variant in the URL
     --------------------------------------------------------------- */

  function syncUrl() {
    var rec = currentRecord();
    if (!rec) return;
    try {
      var u = new URL(location.href);
      if (u.searchParams.get('variant') === String(rec.id)) return;
      u.searchParams.set('variant', rec.id);
      history.replaceState(history.state, '', u.toString());
    } catch (e) { /* history is not critical */ }
  }

  /* ---------------------------------------------------------------
     8. Add to basket without leaving the page
     The section builds a form and submits it, so every add throws the buyer
     onto /cart and back to the default variant. On a cut-to-size product the
     normal behaviour is to order several thicknesses in one go.
     The listener is on document in the capture phase: a listener added on the
     button itself would run after the section's, because for handlers on the
     target element capture and bubble both fire in registration order.
     --------------------------------------------------------------- */

  function qtyValue() {
    var q = document.getElementById('iplpQty');
    return Math.max(1, parseInt(q && q.value, 10) || 1);
  }

  /* the original full-page POST, kept as the fallback so a failed fetch still
     adds the item rather than silently doing nothing */
  function fallbackSubmit(id, qty) {
    var f = document.createElement('form');
    f.method = 'post';
    f.action = root.getAttribute('data-cart-add-url') || CFG.cartAddUrl || '/cart/add';
    [['id', id], ['quantity', qty],
     ['return_to', root.getAttribute('data-cart-url') || CFG.cartUrl || '/cart']
    ].forEach(function (p) {
      var i = document.createElement('input');
      i.type = 'hidden';
      i.name = p[0];
      i.value = p[1];
      f.appendChild(i);
    });
    document.body.appendChild(f);
    f.submit();
  }

  function setCartCount(n) {
    each(document.querySelectorAll('.header__cart-count'), function (el) {
      el.textContent = n;
    });
  }

  function showAdded(qty, count) {
    var el = document.getElementById('iplpAdded');
    if (!el) {
      el = document.createElement('div');
      el.id = 'iplpAdded';
      el.className = 'iplp__added';
      el.setAttribute('role', 'status');
      var anchor = document.getElementById('iplpBuyNow') || root.querySelector('.iplp__buyrow');
      if (!anchor || !anchor.parentNode) return;
      anchor.parentNode.insertBefore(el, anchor.nextSibling);
    }
    var label = selectedValues().filter(Boolean).map(withUnit).join(' / ');
    el.innerHTML = '';

    var p = document.createElement('p');
    p.className = 'iplp__added-msg';
    p.textContent = 'Added: ' + qty + ' × ' + label + '. Your basket now holds ' +
      count + (count === 1 ? ' item.' : ' items.');

    var nav = document.createElement('div');
    nav.className = 'iplp__added-links';
    var go = document.createElement('a');
    go.href = '/checkout';
    go.className = 'iplp__added-go';
    go.textContent = 'Checkout';
    var view = document.createElement('a');
    view.href = root.getAttribute('data-cart-url') || CFG.cartUrl || '/cart';
    view.textContent = 'View basket';
    var keep = document.createElement('button');
    keep.type = 'button';
    keep.className = 'iplp__added-keep';
    keep.textContent = 'Keep shopping';
    keep.addEventListener('click', function () { el.hidden = true; });
    nav.appendChild(go);
    nav.appendChild(view);
    nav.appendChild(keep);

    el.appendChild(p);
    el.appendChild(nav);
    el.hidden = false;
  }

  function interceptAdd(e) {
    var btn = e.target && e.target.closest && e.target.closest('#iplpAtc, #iplpBarAtc');
    if (!btn || btn.disabled) return;
    var rec = currentRecord();
    /* without a known variant id there is nothing to post; leave it to the section */
    if (!rec) return;

    e.preventDefault();
    e.stopPropagation();

    var qty = qtyValue();
    var label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'Adding…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(rec.id), quantity: qty }] })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('add failed: ' + r.status);
        return r.json();
      })
      .then(function () {
        return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.json(); });
      })
      .then(function (cart) {
        btn.disabled = false;
        btn.textContent = label;
        setCartCount(cart.item_count);
        showAdded(qty, cart.item_count);
        lastSaid = '';
        announceSoon();
      })
      .catch(function () {
        fallbackSubmit(rec.id, qty);
      });
  }

  /* ---------------------------------------------------------------
     9. Collapse an empty reviews section
     The Loox section reserves ~300px whether or not there are any reviews.
     textContent is used rather than innerText so the check still works once
     the section has been hidden, and it un-hides if reviews ever arrive.
     Only text and images count as content: Loox injects a tracking <iframe>
     even when it has nothing to show, so treating any iframe as content would
     keep the empty section on screen for ever.
     --------------------------------------------------------------- */

  function collapseEmptyReviews() {
    var sec = document.querySelector('[id*="__reviews"]');
    if (!sec) return;
    function check() {
      var filled = (sec.textContent || '').trim().length > 0 ||
        !!sec.querySelector('img');
      sec.classList.toggle('iplp__empty-section', !filled);
    }
    check();
    var mo = new MutationObserver(check);
    mo.observe(sec, { childList: true, subtree: true });
    setTimeout(function () { mo.disconnect(); check(); }, 12000);
  }

  /* ---------------------------------------------------------------
     4. Panel size to scale
     --------------------------------------------------------------- */

  var scale = null;

  /* the description gives the buyer this sum to do by hand; the page already
     knows both dimensions and the thickness, so it can just state the answer */
  function weightText() {
    if (!(CFG.density > 0)) return '';
    var dim = null;
    var thickness = null;
    selectedValues().forEach(function (v) {
      if (!v) return;
      var d = v.match(DIM);
      if (d) { dim = d; return; }
      var t = v.match(/^\s*([\d.]+)\s*mm\s*$/i);
      if (t) thickness = parseFloat(t[1]);
    });
    if (!dim || !(thickness > 0)) return '';
    var kg = (parseFloat(dim[1]) / 1000) * (parseFloat(dim[2]) / 1000) * thickness * CFG.density;
    if (!(kg > 0)) return '';
    return ' Approx. <b>' + (kg < 10 ? kg.toFixed(2) : kg.toFixed(1)) + ' kg</b>.';
  }

  function readSizes(block) {
    var vals = [];
    each(block.querySelectorAll('[data-optpill]'), function (p) {
      vals.push(p.getAttribute('data-value'));
    });
    if (!vals.length) {
      var s = block.querySelector('[data-optselect]');
      if (s) each(s.options, function (op) { vals.push(op.value); });
    }
    return vals;
  }

  function selectedSize(block) {
    var act = block.querySelector('[data-optpill].active');
    if (act) return act.getAttribute('data-value');
    var s = block.querySelector('[data-optselect]');
    return s ? s.value : null;
  }

  function svgEl(name, attrs) {
    var e = document.createElementNS(SVGNS, name);
    for (var k in attrs) {
      if (Object.prototype.hasOwnProperty.call(attrs, k)) e.setAttribute(k, attrs[k]);
    }
    return e;
  }

  function fmt(n) {
    return n.toLocaleString ? n.toLocaleString('en-GB') : String(n);
  }

  function initScale() {
    var block = null;
    var parsed = null;
    each(optionBlocks(), function (o) {
      if (block) return;
      var vals = readSizes(o).filter(Boolean);
      if (vals.length < 2) return;
      var dims = [];
      vals.forEach(function (v) {
        var m = v.match(DIM);
        if (m) dims.push({ w: parseFloat(m[1]), h: parseFloat(m[2]) });
      });
      /* a majority must be real sizes, so the thickness list can never win, but
         a stray entry like "Custom Size - priced by calculator" is tolerated
         rather than costing the product its drawing */
      if (dims.length >= 2 && dims.length > vals.length / 2) {
        block = o;
        parsed = dims;
      }
    });
    if (!block) return;
    var maxW = Math.max.apply(null, parsed.map(function (p) { return p.w; }));
    var maxH = Math.max.apply(null, parsed.map(function (p) { return p.h; }));
    if (!(maxW > 0) || !(maxH > 0)) return;

    var fig = document.createElement('figure');
    fig.className = 'iplp__scale';

    var svg = svgEl('svg', {
      viewBox: '0 0 ' + maxW + ' ' + maxH,
      preserveAspectRatio: 'xMinYMin meet',
      role: 'img',
      focusable: 'false'
    });
    var title = svgEl('title', {});
    title.textContent = 'The selected panel drawn to scale inside the largest panel size offered';
    svg.appendChild(title);

    svg.appendChild(svgEl('rect', {
      'class': 'iplp__scale-frame', x: 0, y: 0, width: maxW, height: maxH
    }));

    var sel = svgEl('rect', { 'class': 'iplp__scale-sel', x: 0, y: 0, width: 0, height: 0 });
    svg.appendChild(sel);

    /* A4 is only a useful yardstick when the panel is comfortably bigger */
    var a4w = 297;
    var a4h = 210;
    var gap = maxW * 0.02;
    var hasA4 = maxW > a4w * 1.4 && maxH > a4h * 1.4;
    if (hasA4) {
      svg.appendChild(svgEl('rect', {
        'class': 'iplp__scale-a4',
        x: maxW - a4w - gap, y: maxH - a4h - gap, width: a4w, height: a4h
      }));
      var tag = svgEl('text', {
        'class': 'iplp__scale-tag',
        x: maxW - a4w - gap,
        y: maxH - a4h - gap * 1.8,
        'font-size': Math.round(maxW / 30)
      });
      tag.textContent = 'A4';
      svg.appendChild(tag);
    }

    var cap = document.createElement('figcaption');
    fig.appendChild(svg);
    fig.appendChild(cap);
    block.appendChild(fig);

    scale = { block: block, maxW: maxW, maxH: maxH, sel: sel, cap: cap, hasA4: hasA4 };
    drawScale();
  }

  function drawScale() {
    if (!scale) return;
    var val = selectedSize(scale.block);
    var m = val && val.match(DIM);
    if (!m) return;
    var w = parseFloat(m[1]);
    var h = parseFloat(m[2]);
    scale.sel.setAttribute('width', w);
    scale.sel.setAttribute('height', h);

    var area = (w * h) / 1000000;
    var txt = 'Selected panel <b>' + fmt(w) + ' × ' + fmt(h) + ' mm</b> — ' +
      area.toFixed(area < 1 ? 3 : 2) + ' m². Drawn to scale inside the largest size offered (' +
      fmt(scale.maxW) + ' × ' + fmt(scale.maxH) + ' mm)';
    if (scale.hasA4) txt += '; the small outline is a sheet of A4';
    scale.cap.innerHTML = txt + '.' + weightText();
  }

  /* ---------------------------------------------------------------
     wiring
     --------------------------------------------------------------- */

  function refresh() {
    normaliseStock();
    syncPrice();
    /* before markUnavailable, which caches the rendered option labels */
    labelUnits();
    markUnavailable();
    updatePriceExtras();
    syncUrl();
    drawScale();
    announceSoon();
  }

  function start() {
    INDEX = buildIndex();
    upgradeHeadings();
    sortOptionValues();
    initScale();
    collapseEmptyReviews();
    document.addEventListener('click', interceptAdd, true);
    refresh();

    /* price and stock are rewritten on every variant change, so watching them
       covers pills, the select and the dimension axes alike */
    var mo = new MutationObserver(refresh);
    ['iplpBuyPrice', 'iplpStock'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) mo.observe(el, { childList: true, characterData: true, subtree: true });
    });

    /* picking an option that does not change the variant still changes classes */
    each(optionBlocks(), function (o) {
      new MutationObserver(refresh).observe(o, {
        attributes: true, subtree: true, attributeFilter: ['class']
      });
    });

    /* ipl-boost.js injects the fit CTA and the sticky bar after this point */
    var late = new MutationObserver(upgradeHeadings);
    late.observe(root, { childList: true, subtree: true });
    setTimeout(function () { late.disconnect(); }, 6000);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
